import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';

import {
  archiveTransformer,
  createTransformer,
  getStepTypes,
  getTransformer,
  getTransformerVersion,
  listTransformers,
  previewTransformer,
  resolveTransformers,
  saveTransformerVersion,
  updateTransformer,
} from '../services/transformer.service';
import type {
  CreateTransformerRequest,
  PreviewRequest,
  ResolveParams,
  SaveVersionRequest,
  TransformerListParams,
  UpdateTransformerRequest,
} from '../types/transformer.types';
import { transformerErrorCode, transformerErrorMessage } from '../utils/transformer-errors';

/** Layouts and their assignments change rarely mid-session, and every edit here invalidates
 *  them — so a remount or window refocus within this window reuses the cache instead of
 *  refetching (the export-profile sheet reads all three each time it opens). */
const LAYOUT_STALE_MS = 60 * 1000;

/** Library list. Filters are part of the key so each filter combination caches separately. */
export function useTransformers(params: TransformerListParams = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.transformers.list(params),
    queryFn: () => listTransformers(params),
    enabled,
    staleTime: LAYOUT_STALE_MS,
  });
}

/** One transformer with its definition — the editor's saved baseline. */
export function useTransformer(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.transformers.detail(id ?? ''),
    queryFn: () => getTransformer(id ?? ''),
    enabled: enabled && !!id,
    staleTime: LAYOUT_STALE_MS,
  });
}

/** One saved version; versions are immutable, so a fetched one never goes stale. */
export function useTransformerVersion(id: string | undefined, version: number | null) {
  return useQuery({
    queryKey: queryKeys.transformers.version(id ?? '', version ?? 0),
    queryFn: () => getTransformerVersion(id ?? '', version ?? 0),
    enabled: !!id && version !== null,
    staleTime: Infinity,
  });
}

/** Step / writer / file-step registry. Code-defined, so cached for the session. */
export function useStepTypes(enabled = true) {
  return useQuery({
    queryKey: queryKeys.transformers.stepTypes(),
    queryFn: getStepTypes,
    staleTime: Infinity,
    enabled,
  });
}

/**
 * Draft preview. A POST used as a query: the request is the key, so an unchanged
 * draft is never re-run, and the previous result stays on screen while the next
 * one loads (no flash between debounced edits).
 */
export function useTransformerPreview(request: PreviewRequest | null) {
  return useQuery({
    queryKey: queryKeys.transformers.preview(request),
    queryFn: () => {
      if (!request) throw new Error('useTransformerPreview: request required');
      return previewTransformer(request);
    },
    enabled: request !== null,
    placeholderData: keepPreviousData,
    // An answer for a request is final (a draft edit makes a new request): never
    // re-send it on retry, remount or focus — a 404 "no run yet" would loop.
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

/**
 * Resolver — which transformer(s) a subsidiary / connection gets. Open to customers;
 * with `transformerId` (Lily users) it renders that one pick's file names instead.
 */
export function useResolveTransformers(params: ResolveParams | null) {
  return useQuery({
    queryKey: queryKeys.transformers.resolve(
      params?.subsidiaryId ?? '',
      params?.connector ?? '',
      params?.connectionId ?? null,
      params?.transformerId ?? null,
    ),
    queryFn: () => {
      if (!params) throw new Error('useResolveTransformers: params required');
      return resolveTransformers(params);
    },
    enabled: params !== null && params.subsidiaryId !== '',
    retry: false,
    staleTime: LAYOUT_STALE_MS,
  });
}

/** Create a transformer (blank or copied). NAME_TAKEN is left to the dialog to show inline. */
export function useCreateTransformer() {
  const { t } = useTranslation('transformers');
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTransformerRequest) => createTransformer(payload),
    onSuccess: (created) => {
      toast.success(t('toast.created', { name: created.name }));
      void qc.invalidateQueries({ queryKey: queryKeys.transformers.all });
    },
    onError: (err) => {
      if (transformerErrorCode(err) === 'NAME_TAKEN') return;
      toast.error(transformerErrorMessage(err, t('toast.createFailed')));
    },
  });
}

/** Rename / re-describe a transformer. */
export function useUpdateTransformer(id: string) {
  const { t } = useTranslation('transformers');
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateTransformerRequest) => updateTransformer(id, payload),
    onSuccess: () => {
      toast.success(t('toast.updated'));
      void qc.invalidateQueries({ queryKey: queryKeys.transformers.all });
    },
    onError: (err) => toast.error(transformerErrorMessage(err, t('toast.updateFailed'))),
  });
}

/** Save the draft as the next version; assignments on "Latest" pick it up immediately. */
export function useSaveTransformerVersion(id: string) {
  const { t } = useTranslation('transformers');
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveVersionRequest) => saveTransformerVersion(id, payload),
    onSuccess: (saved) => {
      toast.success(t('toast.versionSaved', { version: saved.currentVersion }));
      qc.setQueryData(queryKeys.transformers.detail(id), saved);
      void qc.invalidateQueries({ queryKey: queryKeys.transformers.all });
    },
    onError: (err) => toast.error(transformerErrorMessage(err, t('toast.saveFailed'))),
  });
}

/** Archive. 409 IN_USE gets its own toast: the fix is removing the assignments first. */
export function useArchiveTransformer() {
  const { t } = useTranslation('transformers');
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; name: string }) => archiveTransformer(vars.id),
    onSuccess: (_data, vars) => {
      toast.success(t('toast.archived', { name: vars.name }));
      void qc.invalidateQueries({ queryKey: queryKeys.transformers.all });
    },
    onError: (err) => {
      if (transformerErrorCode(err) === 'IN_USE') {
        toast.warning(t('toast.archiveInUse'));
        return;
      }
      toast.error(transformerErrorMessage(err, t('toast.archiveFailed')));
    },
  });
}
